import { client } from '@/api/orpc'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useUser } from './user'

export const useInventory = defineStore('inventory', () => {
	const loading = ref(true)
	const items = ref<
		{
			uuid: string
			modelUuid: string
			modelName: string
			collectionName: string
			price: number
			preloader: string | null
			claiming: boolean
			claimable: boolean
			error?:
				| 'UNAVAILABLE'
				| 'PRICE_MISMATCH'
				| 'INTERNAL_ERROR'
				| 'BOT_INTERACTION_REQUIRED'
		}[]
	>([])

	const claimInteractionModal = ref(false)
	const openClaimInteractionModal = () => {
		claimInteractionModal.value = true
	}

	const queuedUpdates = ref<
		{
			uuid: string
			errorCode?:
				| 'UNAVAILABLE'
				| 'PRICE_MISMATCH'
				| 'INTERNAL_ERROR'
				| 'BOT_INTERACTION_REQUIRED'
		}[]
	>([])

	const updateItems = (
		updatedItems: {
			uuid: string
			errorCode?:
				| 'UNAVAILABLE'
				| 'PRICE_MISMATCH'
				| 'INTERNAL_ERROR'
				| 'BOT_INTERACTION_REQUIRED'
		}[]
	) => {
		if (loading.value) {
			queuedUpdates.value.push(...updatedItems)
			return
		}

		for (const item of updatedItems) {
			const localItem = items.value.find((i) => i.uuid === item.uuid)

			if (!localItem) {
				continue
			}

			if (item.errorCode) {
				localItem.error = item.errorCode
				localItem.claiming = false
				continue
			}

			items.value = items.value.filter((i) => i.uuid !== item.uuid)
		}
	}

	const getItems = async () => {
		loading.value = true

		const { error, data } = await client.user.getInventory()

		loading.value = false

		if (error) {
			return console.error(error)
		}
		if (!data) {
			return
		}

		items.value = data

		if (queuedUpdates.value.length > 0) {
			updateItems(queuedUpdates.value)
			queuedUpdates.value = []
		}
	}

	const itemsCount = computed(() => items.value.length)

	const pushItems = (
		newItems: {
			uuid: string
			modelUuid: string
			modelName: string
			collectionName: string
			price: number
			preloader: string | null
			claimable: boolean
		}[]
	) => {
		items.value.unshift(
			...newItems
				.slice(0)
				.map((item) => ({ ...item, claiming: false }))
				.reverse()
		)
	}

	const user = useUser()

	const sellItems = async (uuids: string[]) => {
		if (!uuids.length) {
			return []
		}

		const { error, data } = await client.game.sellItem({
			uuids: uuids.length === items.value.length ? [] : uuids.slice(0, 200)
		})

		if (error) {
			console.error(error)
			return []
		}

		if (!data) {
			return []
		}

		user.setBalance(data.updatedUser.coins)
		items.value = items.value.filter(
			(item) => !data.soldItems.includes(item.uuid)
		)

		return data.soldItems
	}

	const resetItems = (uuids: string[]) => {
		for (let i = 0; i < items.value.length; i++) {
			if (!uuids.includes(items.value[i].uuid)) {
				continue
			}

			items.value[i].claiming = false
			items.value[i].error = undefined
		}
	}

	const claimItems = async (uuids: string[]) => {
		for (let i = 0; i < items.value.length; i++) {
			if (!uuids.includes(items.value[i].uuid)) {
				continue
			}

			items.value[i].claiming = true
			items.value[i].error = undefined
		}

		const { error, data } = await client.market.claimInventoryItems({
			uuids: uuids.length === items.value.length ? [] : uuids.slice(0, 200)
		})

		if (error) {
			resetItems(uuids)
			console.error(error)

			return false
		}
		if (!data) {
			return false
		}

		updateItems(data.inventoryItems.filter((item) => !!item.errorCode))

		if (
			data.inventoryItems.some(
				(item) => item.errorCode === 'BOT_INTERACTION_REQUIRED'
			)
		) {
			openClaimInteractionModal()
		}

		return true
	}

	return {
		loading,
		getItems,
		items,
		itemsCount,
		sellItems,
		pushItems,
		claimItems,
		resetItems,
		updateItems,
		claimInteractionModal,
		openClaimInteractionModal
	}
})
